import React, { useState, useEffect, useRef } from "react";
import { FilterMatchMode } from "primereact/api";
import { Toast } from "primereact/toast";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./Users.css";
import usePageTitle from "../usePageTitle";
import UserTable from "./UserTable";
import UserDialog from "./UserDialog";
import AddUserDialog from "./AddUserDialog";
import Header from "./Header";
import { fetchUsers, saveNewUser } from "./api";

const Users = () => {
  usePageTitle("Users - SkeyeForm™");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(7);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.EQUALS },
  });
  const [dialogVisible, setDialogVisible] = useState(false);
  //eslint-disable-next-line
  const [selectedUser, setSelectedUser] = useState(null);
  const [addUserDialogVisible, setAddUserDialogVisible] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    roles: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useRef(null);

  const refreshUsers = async () => {
    try {
      // Set loading state to prevent showing old data
      setFilteredData([]); // Clear current data while loading

      const usersData = await fetchUsers();
      setData(usersData);
      setTotalRecords(usersData.length);

      // Apply filtering but don't update the UI yet
      let filtered = usersData;
      if (globalFilterValue) {
        filtered = usersData.filter((user) => {
          return (
            String(user.id)
              .toLowerCase()
              .includes(globalFilterValue.toLowerCase()) ||
            user.username
              .toLowerCase()
              .includes(globalFilterValue.toLowerCase()) ||
            user.email
              .toLowerCase()
              .includes(globalFilterValue.toLowerCase()) ||
            user.roles.some((role) =>
              role.name.toLowerCase().includes(globalFilterValue.toLowerCase())
            )
          );
        });
        setTotalRecords(filtered.length);
      }

      // Only update filteredData with the current page's worth of data
      const paginatedData = filtered.slice(first, first + rows);
      setFilteredData(paginatedData);
    } catch (error) {
      console.error("Error refreshing users:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to refresh users",
        life: 3000,
      });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await refreshUsers();
      setIsLoading(false);
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, rows]);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const filtered = data.filter((user) => {
      return (
        !globalFilterValue ||
        (user.id && user.id.toString().includes(globalFilterValue)) ||
        (user.username &&
          user.username
            .toLowerCase()
            .includes(globalFilterValue.toLowerCase())) ||
        (user.email &&
          user.email.toLowerCase().includes(globalFilterValue.toLowerCase())) ||
        (user.modules &&
          user.modules.toLowerCase().includes(globalFilterValue.toLowerCase()))
      );
    });

    // Update total records for pagination
    setTotalRecords(filtered.length);

    // Only slice the data for the current page
    setFilteredData(filtered.slice(first, first + rows));
  }, [globalFilterValue, first, rows, data]);

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setFirst(0);
  };

  const clearFilter = () => {
    setGlobalFilterValue("");
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
  };

  const openAddUserDialog = () => {
    setAddUserDialogVisible(true);
  };

  const openDialog = (user) => {
    setSelectedUser(user);
    setDialogVisible(true);
  };

  const header = (
    <Header
      globalFilterValue={globalFilterValue}
      onGlobalFilterChange={onGlobalFilterChange}
      clearFilter={clearFilter}
      openAddUserDialog={openAddUserDialog}
    />
  );

  return (
    <div className="p-grid p-dir-col table-page">
      <Toast ref={toast} />
      <div className="p-col title">
        <h2>Users</h2>
        <div className="title-bar"></div>
      </div>
      <UserTable
        filteredData={filteredData}
        filters={filters}
        header={header}
        onFilter={setFilters}
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        openDialog={openDialog}
        refreshUsers={refreshUsers}
        loading={isLoading}
      />
      <UserDialog
        dialogVisible={dialogVisible}
        setDialogVisible={setDialogVisible}
        selectedUser={selectedUser}
        refreshUsers={refreshUsers}
      />
      <AddUserDialog
        addUserDialogVisible={addUserDialogVisible}
        setAddUserDialogVisible={setAddUserDialogVisible}
        newUser={newUser}
        setNewUser={setNewUser}
        saveNewUser={() =>
          saveNewUser(
            newUser,
            toast,
            setAddUserDialogVisible,
            setNewUser,
            refreshUsers
          )
        }
        toast={toast}
      />
    </div>
  );
};

export default Users;
